export const GIT_ACTION_PROGRESS_PRESENTATION_LIMIT_MS = 30_000;

export interface GitActionNotificationLifecycle<NotificationId> {
  readonly actionStartedAtMs: number;
  readonly progressAttached: boolean;
  updateProgress(update: (notificationId: NotificationId) => void): void;
  publishResult(input: {
    updateAttached: (notificationId: NotificationId, closeResult: () => void) => void;
    addDetached: (closeResult: () => void) => NotificationId;
  }): NotificationId;
  interrupt(): void;
}

export function createGitActionNotificationLifecycle<NotificationId>(input: {
  readonly actionStartedAtMs: number;
  readonly progressNotificationId: NotificationId;
  readonly closeNotification: (notificationId: NotificationId) => void;
}): GitActionNotificationLifecycle<NotificationId> {
  let state: "attached" | "detached" | "settled" = "attached";

  const detachProgress = () => {
    if (state !== "attached") {
      return;
    }
    state = "detached";
    input.closeNotification(input.progressNotificationId);
  };

  const elapsedMs = Math.max(0, Date.now() - input.actionStartedAtMs);
  const presentationTimeout = globalThis.setTimeout(
    detachProgress,
    Math.max(0, GIT_ACTION_PROGRESS_PRESENTATION_LIMIT_MS - elapsedMs),
  );

  return {
    actionStartedAtMs: input.actionStartedAtMs,
    get progressAttached() {
      return state === "attached";
    },
    updateProgress(update) {
      if (state === "attached") {
        update(input.progressNotificationId);
      }
    },
    publishResult({ updateAttached, addDetached }) {
      globalThis.clearTimeout(presentationTimeout);
      const wasAttached = state === "attached";
      state = "settled";

      let resultNotificationId = input.progressNotificationId;
      const closeResult = () => {
        input.closeNotification(resultNotificationId);
      };

      if (wasAttached) {
        updateAttached(input.progressNotificationId, closeResult);
      } else {
        resultNotificationId = addDetached(closeResult);
      }
      return resultNotificationId;
    },
    interrupt() {
      globalThis.clearTimeout(presentationTimeout);
      detachProgress();
    },
  };
}
